<template>
  <div
    class="bg-white dark:bg-gray-900 rounded-lg shadow hover:shadow-lg transition-all duration-200 p-4 sm:p-5 relative border border-gray-200 dark:border-gray-700 h-full flex flex-col"
  >
    <!-- Actions menu - Always positioned at top right -->
    <div class="absolute top-4 right-4 sm:top-6 sm:right-6">
      <DropdownMenu
        :id="`rule-menu-${rule.id}`"
        button-label="Actions"
        width="sm"
      >
        <DropdownMenuItem
          :icon="PencilIcon"
          variant="default"
          @click="$emit('edit', rule)"
        >
          Editar
        </DropdownMenuItem>

        <DropdownMenuItem
          :icon="rule.is_active ? PauseIcon : PlayIcon"
          :variant="rule.is_active ? 'warning' : 'success'"
          @click="$emit('toggle-status', rule)"
        >
          {{ rule.is_active ? 'Desactivar' : 'Activar' }}
        </DropdownMenuItem>

        <DropdownMenuDivider />

        <DropdownMenuItem
          :icon="TrashIcon"
          variant="danger"
          @click="$emit('delete', rule)"
        >
          Eliminar
        </DropdownMenuItem>
      </DropdownMenu>
    </div>

    <!-- Main content with right padding to avoid overlap with menu -->
    <div class="pr-12 sm:pr-14 flex-1 flex flex-col">
      <!-- Title and status badges -->
      <div class="flex flex-col gap-3">
        <h3 class="text-base font-semibold text-gray-900 dark:text-white line-clamp-2">
          {{ rule.name }}
        </h3>

        <!-- Status badges -->
        <div class="flex flex-wrap gap-2">
          <span
            :class="[
              'px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap',
              rule.is_active
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
            ]"
          >
            {{ rule.is_active ? 'Activa' : 'Inactiva' }}
          </span>
          <span
            class="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 whitespace-nowrap"
          >
            {{ getRuleTypeLabel(rule.rule_type) }}
          </span>
          <span
            class="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 whitespace-nowrap"
          >
            Prioridad: {{ rule.priority }}
          </span>
        </div>
      </div>

      <!-- Configuration -->
      <div class="mt-auto pt-3">
        <div class="text-sm font-medium text-gray-900 dark:text-white mb-1">
          Configuración:
        </div>
        <div class="text-sm text-gray-600 dark:text-gray-400 break-words rounded p-2">
          {{ formatRuleConfig(rule.rule_type, rule.rule_config) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { PencilIcon, PlayIcon, PauseIcon, TrashIcon } from '@heroicons/vue/24/outline'
import DropdownMenu from '@/components/ui/DropdownMenu.vue'
import DropdownMenuItem from '@/components/ui/DropdownMenuItem.vue'
import DropdownMenuDivider from '@/components/ui/DropdownMenuDivider.vue'
import type { DeliveryFeeRule } from '@/composables/useDeliveryFeeRules'

interface Props {
  rule: DeliveryFeeRule
  getRuleTypeLabel: (type: string) => string
  formatRuleConfig: (type: string, config: Record<string, any>) => string
}

defineProps<Props>()

defineEmits<{
  edit: [rule: DeliveryFeeRule]
  'toggle-status': [rule: DeliveryFeeRule]
  delete: [rule: DeliveryFeeRule]
}>()
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

@media (min-width: 640px) {
  .sm\:line-clamp-none {
    display: block;
    -webkit-line-clamp: unset;
    -webkit-box-orient: unset;
    overflow: visible;
  }
}
</style>
